/**
 * niftiCore.js — чистый построитель байтов NIfTI-1 из niftiModule.exportToNifti.
 *
 * Перенесено 1:1 из тела exportToNifti (сверено со спецификацией NIfTI-1): убраны
 * только Blob/URL/DOM-скачивание и телеметрия — они к формату отношения не имеют.
 * Возвращает ArrayBuffer готового .nii. Смещения полей НЕ менял.
 *
 * Правильный шаг дальше — чтобы niftiModule.exportToNifti собирал буфер отсюда, а
 * сам занимался только скачиванием. Сейчас это зеркало для проверки.
 */

/**
 * [11.1.70] МЕТКА «ОТКУДА ЭТА МАСКА» — поле descrip заголовка NIfTI.
 *
 * ПОВОД, слова владельца: «я делал эту маску на 818 срезах, значит после
 * импорта должна открыться именно эта серия». До этой версии в файле НЕ БЫЛО
 * НИЧЕГО, что указывало бы на серию: только размеры, шаг вокселя и положение.
 * По числу срезов серию не угадать — в одном исследовании их может быть
 * несколько с одинаковой сеткой.
 *
 * Место: смещение 148, 80 байт, поле descrip стандарта NIfTI-1. Оно для
 * свободного текста и предназначено; чужие программы его показывают, а не
 * ломаются на нём. Раньше мы оставляли его нулями.
 *
 * Формат нарочно простой и разбираемый глазами:
 *     mikhmel|<seriesUID>|<описание серии>
 * Разделитель — вертикальная черта: в UID её не бывает, а в описании серии
 * встречаться не должна, и на всякий случай мы её оттуда вычищаем.
 *
 * 80 байт — тесно. UID приоритетнее описания: по нему серия ищется, описание
 * нужно только чтобы сказать человеку, какую серию открыть. Поэтому описание
 * обрезается первым.
 */
export const МЕТКА_ЗНАК = 'mikhmel';
export const DESCRIP_СМЕЩЕНИЕ = 148;
export const DESCRIP_ДЛИНА = 80;

/** Собрать метку, уложившись в 80 байт (только ASCII — descrip его и ждёт). */
export function собратьМетку(seriesUID, описание) {
  const чисто = (с) => String(с || '').replace(/[|\r\n\0]/g, ' ').trim();
  // [11.1.70] ТОЛЬКО ASCII, И ДЛЯ UID ТОЖЕ.
  //
  // Поймано своим же тестом: в первой версии я вычищал не-ASCII только из
  // ОПИСАНИЯ, а UID писал как есть. Поле descrip хранит по одному байту на
  // знак, и «UID-исследование-A» прочиталось обратно как «UID-8AA;54>20=85-A».
  // В настоящих DICOM-идентификаторах кириллицы не бывает, поэтому у владельца
  // это не всплыло бы — но полагаться на «не бывает» нельзя: искажённый UID
  // значит, что серия НЕ НАЙДЁТСЯ, и вместо переключения врач получит «серии
  // нет среди открытых». Тихая подмена причины отказа.
  const тольколатиница = (с) => с.replace(/[^\x20-\x7E]/g, '');
  const uid = тольколатиница(чисто(seriesUID));
  let опис = тольколатиница(чисто(описание));
  let метка = `${МЕТКА_ЗНАК}|${uid}|${опис}`;
  if (метка.length > DESCRIP_ДЛИНА - 1) {
    // Режем ОПИСАНИЕ, не UID: по UID ищется серия, описание — только для глаз.
    const запас = DESCRIP_ДЛИНА - 1 - (`${МЕТКА_ЗНАК}|${uid}|`).length;
    метка = `${МЕТКА_ЗНАК}|${uid}|` + (запас > 0 ? опис.slice(0, запас) : '');
  }
  return метка.slice(0, DESCRIP_ДЛИНА - 1);
}

/** Прочитать метку из заголовка. Нет метки — вернуть null, а не выдумать. */
export function прочитатьМетку(buffer) {
  try {
    if (!buffer || buffer.byteLength < DESCRIP_СМЕЩЕНИЕ + DESCRIP_ДЛИНА) return null;
    const б = new Uint8Array(buffer, DESCRIP_СМЕЩЕНИЕ, DESCRIP_ДЛИНА);
    let текст = '';
    for (let i = 0; i < б.length; i++) {
      if (б[i] === 0) break;
      текст += String.fromCharCode(б[i]);
    }
    if (!текст.startsWith(`${МЕТКА_ЗНАК}|`)) return null;
    const ч = текст.split('|');
    const uid = (ч[1] || '').trim();
    if (!uid) return null;
    return { seriesUID: uid, описание: (ч[2] || '').trim() };
  } catch (e) { return null; }
}

/**
 * [12.0.54] ПОЛОЖЕНИЕ МАСКИ В ТЕЛЕ — sform и qform заголовка.
 *
 * До этой версии экспорт писал только размеры и шаг: оба кода ориентации
 * нулевые (verify_11_1_76 так и записал: «в нашем файле матрицы ориентации
 * нет — и мы это признаём»). Наш же импорт такой файл кладёт «как есть» — для
 * своих файлов это верно. А 3D Slicer, ITK-SNAP и прочие, не найдя матрицы,
 * ставят маску по своему умолчанию: в начало координат и без поворота — мимо
 * снимка, а то и зеркально. Экспорт для того и нужен, чтобы маску можно было
 * открыть в другой программе вместе с серией.
 *
 * Теперь в файл пишется настоящая геометрия тома маски — начало, направление
 * осей и шаг — в RAS, как требует стандарт NIfTI-1 (Cornerstone живёт в LPS:
 * у первых двух осей мира знак обратный). Тот же перевод, что читает
 * core/niftiOrient.js, только в обратную сторону; проверено туда-обратно
 * (tests/segtab_test.mjs).
 *
 * @param {number[]} spacing @param {number[]} origin — LPS, мм
 * @param {number[]} direction — 9 чисел Cornerstone: ось i, ось j, ось k (LPS)
 * @returns {number[][]|null} 3 строки по 4 числа: [R|t] в RAS; null — геометрия не читается
 */
export function аффинRAS(spacing, origin, direction) {
  const s = Array.from(spacing || []).map(Number);
  const o = Array.from(origin || []).map(Number);
  const d = Array.from(direction || []).map(Number);
  if (s.length !== 3 || o.length !== 3 || d.length !== 9 || ![...s, ...o, ...d].every(Number.isFinite)) return null;
  const знак = [-1, -1, 1];   // LPS → RAS
  return [0, 1, 2].map((р) => [
    знак[р] * d[р] * s[0], знак[р] * d[3 + р] * s[1], знак[р] * d[6 + р] * s[2], знак[р] * o[р],
  ]);
}

/**
 * Кватернион qform из поворотной части матрицы (как mat44_to_quatern в
 * nifti1_io.c — эталонной библиотеке NIfTI). Левая тройка осей
 * (определитель < 0) записывается через qfac = −1 с отражённой третьей осью.
 * @param {number[][]} m — 3×3 (или 3×4), столбцы = оси вокселей, с шагом
 * @returns {{b:number, c:number, d:number, qfac:1|-1}|null}
 */
export function кватернион(m) {
  if (!Array.isArray(m) || m.length !== 3) return null;
  const к = [0, 1, 2].map((ст) => [m[0][ст], m[1][ст], m[2][ст]]);   // столбцы
  const дл = к.map((в) => Math.hypot(...в));
  if (дл.some((ч) => !(ч > 0))) return null;
  const R = [0, 1, 2].map((р) => [0, 1, 2].map((ст) => к[ст][р] / дл[ст]));
  const опр = R[0][0] * (R[1][1] * R[2][2] - R[1][2] * R[2][1])
    - R[0][1] * (R[1][0] * R[2][2] - R[1][2] * R[2][0])
    + R[0][2] * (R[1][0] * R[2][1] - R[1][1] * R[2][0]);
  let qfac = 1;
  if (опр < 0) { qfac = -1; for (let р = 0; р < 3; р++) R[р][2] = -R[р][2]; }
  const [[r11, r12, r13], [r21, r22, r23], [r31, r32, r33]] = R;
  let a = r11 + r22 + r33 + 1, b, c, d;
  if (a > 0.5) {
    a = 0.5 * Math.sqrt(a);
    b = 0.25 * (r32 - r23) / a; c = 0.25 * (r13 - r31) / a; d = 0.25 * (r21 - r12) / a;
  } else {
    const xd = 1 + r11 - (r22 + r33), yd = 1 + r22 - (r11 + r33), zd = 1 + r33 - (r11 + r22);
    if (xd > 1) {
      b = 0.5 * Math.sqrt(xd); c = 0.25 * (r12 + r21) / b; d = 0.25 * (r13 + r31) / b; a = 0.25 * (r32 - r23) / b;
    } else if (yd > 1) {
      c = 0.5 * Math.sqrt(yd); b = 0.25 * (r12 + r21) / c; d = 0.25 * (r23 + r32) / c; a = 0.25 * (r13 - r31) / c;
    } else {
      d = 0.5 * Math.sqrt(zd); b = 0.25 * (r13 + r31) / d; c = 0.25 * (r23 + r32) / d; a = 0.25 * (r21 - r12) / d;
    }
    if (a < 0) { b = -b; c = -c; d = -d; }
  }
  return { b, c, d, qfac };
}

/**
 * @param {ArrayLike<number>} pixels @param {number[]} dims @param {number[]} spacing
 * @param {string|null} метка — [11.1.70] откуда маска (descrip)
 * @param {{origin:number[], direction:number[]}|null} геометрия — [12.0.54] положение тома
 *        маски (LPS, как в Cornerstone); без неё коды ориентации остаются нулевыми, как прежде
 */
export function buildNiftiBytes(pixels, dims, spacing, метка = null, геометрия = null) {
  if (!pixels || !dims || dims.length !== 3 || !spacing || spacing.length !== 3) {
    throw new Error('Invalid input for NIfTI export. Required: pixels, 3D dims, 3D spacing');
  }
  const [nx, ny, nz] = dims;
  const [sx, sy, sz] = spacing;

  const HEADER_SIZE = 348;
  const EXTENSION_SIZE = 4;
  const DATA_OFFSET = 352;
  const BYTES_PER_VOXEL = 2; // INT16 (Datatype 4)

  const dataSize = pixels.length * BYTES_PER_VOXEL;
  const buffer = new ArrayBuffer(HEADER_SIZE + EXTENSION_SIZE + dataSize);
  const view = new DataView(buffer);

  view.setInt32(0, HEADER_SIZE, true);      // sizeof_hdr

  view.setInt16(40, 3, true);               // dim[0] (ndim = 3)
  view.setInt16(42, nx, true);              // dim[1]
  view.setInt16(44, ny, true);              // dim[2]
  view.setInt16(46, nz, true);              // dim[3]
  view.setInt16(48, 1, true);               // dim[4]
  view.setInt16(50, 1, true);               // dim[5]
  view.setInt16(52, 1, true);               // dim[6]
  view.setInt16(54, 1, true);               // dim[7]

  view.setInt16(70, 4, true);               // datatype: 4 = INT16
  view.setInt16(72, 16, true);              // bitpix

  view.setFloat32(76, 1.0, true);           // pixdim[0] (qfac)
  view.setFloat32(80, sx, true);            // pixdim[1]
  view.setFloat32(84, sy, true);            // pixdim[2]
  view.setFloat32(88, sz, true);            // pixdim[3]

  view.setFloat32(108, DATA_OFFSET, true);  // vox_offset
  view.setFloat32(112, 1.0, true);          // scl_slope
  view.setFloat32(116, 0.0, true);          // scl_inter

  view.setUint8(123, 0x02);                 // xyzt_units: 2 = мм

  // [12.0.54] Положение в теле: sform (матрица целиком) и qform (кватернион).
  // Коды 1 = «координаты сканера» — так пишет и dcm2niix.
  const аф = геометрия ? аффинRAS(spacing, геометрия.origin, геометрия.direction) : null;
  const кв = аф ? кватернион(аф) : null;
  if (аф && кв) {
    view.setInt16(252, 1, true);                          // qform_code
    view.setInt16(254, 1, true);                          // sform_code
    view.setFloat32(76, кв.qfac, true);                   // pixdim[0] = qfac
    view.setFloat32(256, кв.b, true);                     // quatern_b
    view.setFloat32(260, кв.c, true);                     // quatern_c
    view.setFloat32(264, кв.d, true);                     // quatern_d
    view.setFloat32(268, аф[0][3], true);                 // qoffset_x
    view.setFloat32(272, аф[1][3], true);                 // qoffset_y
    view.setFloat32(276, аф[2][3], true);                 // qoffset_z
    for (let р = 0; р < 3; р++) {
      for (let ст = 0; ст < 4; ст++) view.setFloat32(280 + р * 16 + ст * 4, аф[р][ст], true);   // srow_x/y/z
    }
  }

  // [11.1.70] Метка «откуда маска» в descrip. Нет метки — поле остаётся
  // нулями, как было: чужие файлы от этого не страдают.
  if (метка) {
    const строка = String(метка).slice(0, DESCRIP_ДЛИНА - 1);
    for (let i = 0; i < строка.length; i++) {
      view.setUint8(DESCRIP_СМЕЩЕНИЕ + i, строка.charCodeAt(i) & 0x7F);
    }
    view.setUint8(DESCRIP_СМЕЩЕНИЕ + строка.length, 0);
  }

  view.setUint8(344, 0x6E); // n
  view.setUint8(345, 0x2B); // +
  view.setUint8(346, 0x31); // 1
  view.setUint8(347, 0x00); // \0

  view.setInt32(HEADER_SIZE, 0, true);      // extension flag

  const dataArray = new Int16Array(buffer, DATA_OFFSET);
  if (pixels instanceof Int16Array) {
    dataArray.set(pixels);
  } else {
    for (let i = 0; i < pixels.length; i++) dataArray[i] = Math.round(pixels[i]);
  }
  return buffer;
}

/** Независимый разбор заголовка обратно — для сверки со спецификацией. */
export function parseNiftiHeader(buffer) {
  const v = new DataView(buffer);
  const magic = String.fromCharCode(v.getUint8(344), v.getUint8(345), v.getUint8(346), v.getUint8(347));
  return {
    sizeofHdr: v.getInt32(0, true),
    dim: [v.getInt16(40, true), v.getInt16(42, true), v.getInt16(44, true), v.getInt16(46, true),
          v.getInt16(48, true), v.getInt16(50, true), v.getInt16(52, true), v.getInt16(54, true)],
    datatype: v.getInt16(70, true),
    bitpix: v.getInt16(72, true),
    pixdim: [v.getFloat32(76, true), v.getFloat32(80, true), v.getFloat32(84, true), v.getFloat32(88, true)],
    voxOffset: v.getFloat32(108, true),
    sclSlope: v.getFloat32(112, true),
    sclInter: v.getFloat32(116, true),
    xyztUnits: v.getUint8(123),
    magic,
  };
}

export function readVoxels(buffer) { return new Int16Array(buffer, 352); }

export default { buildNiftiBytes, parseNiftiHeader, readVoxels, аффинRAS, кватернион };
